/**
 * AdaptivePerfGovernor — the single shared governor for the adaptive budget.
 *
 * Render it once inside any editor's <Canvas>. It samples the per-frame delta,
 * smooths it into an FPS estimate, and tunes the global PERF budget. It renders
 * nothing. Stall/background frames (huge deltas) are ignored so they don't yank
 * the budget down.
 *
 * It also exposes a zero-cost live probe at `window.__synapsePerf` (fps, draw
 * calls, triangles, current budget) — read it from the devtools console to
 * measure a scene: `JSON.stringify(window.__synapsePerf)`.
 */

import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { PERF, RENDER_STATS, tunePerfBudget, setObstacleDensity, proactiveViewBeats } from './adaptiveBudget';
import { initPerfTelemetry, perfTelemetryTick, perfTelemetryFrame } from './perfTelemetry';
import { useEditorStore } from '../store/editorStore';

interface PerfProbe {
  fps: number;
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
  maxNotes: number;
  maxObstacles: number;
  /** Adaptive draw distances (beats ahead) — shrink in dense sections. */
  noteViewBeats: number;
  obstacleViewBeats: number;
  maxNotesVR: number;
  scale: number;
  renderScale: number;
  dpr: number;
  // Per-category visible/mounted counts (what's actually being drawn).
  obsVisible: number;
  obsMounted: number;
  notesDrawn: number;
  rails: number;
}

export default function AdaptivePerfGovernor() {
  const ema = useRef(60);
  const gl = useThree(s => s.gl);
  const scene = useThree(s => s.scene);
  const camera = useThree(s => s.camera);
  // NOTE: the governor does NOT touch the pixel ratio. PERF.renderScale is
  // applied by each editor through <Canvas dpr={useScaledDpr(...)}>, because
  // R3F re-asserts that prop on every Canvas render — an imperative setDpr()
  // here just started a tug-of-war, and every round trip reallocated the
  // drawing buffer (one blank frame each), which is exactly the whole-editor
  // flash reported in Perf/Auto mode. See perf/useScaledDpr.ts.

  // Debug handle next to __synapsePerf: the live renderer/scene/camera, so a
  // perf problem can be bisected from the console (count meshes, toggle a
  // group's visibility, read gl.info) without adding throwaway instrumentation.
  useEffect(() => {
    (window as unknown as { __synapseScene?: unknown }).__synapseScene = { gl, scene, camera };
    return () => { delete (window as unknown as { __synapseScene?: unknown }).__synapseScene; };
  }, [gl, scene, camera]);

  // Swallow REDUNDANT renderer resizes. three's setSize assigns canvas.width/
  // height unconditionally, and assigning the SAME value still clears the
  // canvas (one blank frame). R3F's resize plumbing was observed calling
  // setPixelRatio with an unchanged value several times in a row around mode
  // switches — each one a gratuitous flash. Values that actually change still
  // go straight through.
  useEffect(() => {
    const g = gl as unknown as {
      __resizeDeduped?: boolean;
      setPixelRatio: (v: number) => void;
      setSize: (w: number, h: number, updateStyle?: boolean) => void;
      getPixelRatio: () => number;
      domElement: HTMLCanvasElement;
    };
    if (g.__resizeDeduped) return;
    g.__resizeDeduped = true;
    const nativePR = g.setPixelRatio.bind(gl);
    const nativeSize = g.setSize.bind(gl);
    g.setPixelRatio = (v: number) => {
      if (Math.abs(g.getPixelRatio() - v) < 1e-3) return;
      nativePR(v);
    };
    g.setSize = (w: number, h: number, updateStyle?: boolean) => {
      // Compare against the drawing buffer (logical size × ratio) — cheap and
      // exact. Skipping an identical call is safe: it would only re-set the
      // same canvas dims (clearing it) and re-apply the same style.
      const pr = g.getPixelRatio();
      if (g.domElement.width === Math.floor(w * pr) && g.domElement.height === Math.floor(h * pr)) return;
      nativeSize(w, h, updateStyle);
    };
    // Intentionally never restored: the patch is idempotent, keyed on the gl
    // instance, and dies with the canvas.
  }, [gl]);

  // Per-user performance telemetry (GPU + FPS profile → admin dashboard panel).
  useEffect(() => { initPerfTelemetry(gl); }, [gl]);
  // One stable object mutated in place — no per-frame allocation.
  const probe = useRef<PerfProbe>({
    fps: 60, calls: 0, triangles: 0, geometries: 0, textures: 0,
    maxNotes: 0, maxObstacles: 0, noteViewBeats: 0, obstacleViewBeats: 0, maxNotesVR: 0, scale: 1,
    renderScale: 1, dpr: 1,
    obsVisible: 0, obsMounted: 0, notesDrawn: 0, rails: 0,
  });

  // Precompute where the obstacles are (per difficulty) so the governor can
  // pre-cap the budget as the playhead approaches a dense stretch. Notes are
  // instanced (cheap) — obstacles are the real budget lever.
  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  useEffect(() => {
    const diff = beatmap?.difficulties[activeDifficulty];
    if (!diff) { setObstacleDensity([]); return; }
    const bpm = beatmap?.bpm || 120;
    const toBeat = (o: { time_beats?: number; time_ms: number }) =>
      o.time_beats ?? ((o.time_ms / 1000) * (bpm / 60));
    const beats: number[] = [];
    for (const s of diff.slides || []) beats.push(toBeat(s));
    for (const o of diff.squares || []) beats.push(toBeat(o));
    for (const o of diff.triangles || []) beats.push(toBeat(o));
    beats.sort((a, b) => a - b);
    setObstacleDensity(beats);
  }, [beatmap, activeDifficulty]);

  useFrame((_, delta) => {
    // Hitch telemetry wants the RAW delta — the stall filter below exists
    // precisely to hide the frames the hitch counter must see.
    perfTelemetryFrame(delta);
    // Ignore stalls / backgrounded frames (>200ms) — they aren't a real FPS drop.
    if (delta > 0.2 || delta <= 0) return;
    const fps = Math.min(144, 1 / delta);
    // Smooth so one janky frame doesn't move the budget; ~0.5s response.
    ema.current = ema.current * 0.92 + fps * 0.08;
    // Proactive: shorten the obstacle draw distance as a dense stretch approaches
    // (the window already looks ~50 beats ahead, so this pulls the horizon in
    // before those obstacles get close) — the framerate never has to drop first.
    const beat = useEditorStore.getState().playback.currentBeat;
    tunePerfBudget(ema.current, performance.now(), proactiveViewBeats(beat));
    perfTelemetryTick(gl, ema.current);   // internally 1Hz — no per-frame cost

    // Live probe (reads LAST frame's renderer counters — good enough).
    const p = probe.current;
    p.fps = Math.round(ema.current);
    p.calls = gl.info.render.calls;
    p.triangles = gl.info.render.triangles;
    p.geometries = gl.info.memory.geometries;
    p.textures = gl.info.memory.textures;
    p.maxNotes = PERF.maxNotes;
    p.maxObstacles = PERF.maxObstacles;
    p.noteViewBeats = Math.round(PERF.noteViewBeats);
    p.obstacleViewBeats = Math.round(PERF.obstacleViewBeats);
    p.maxNotesVR = PERF.maxNotesVR;
    p.scale = PERF.scale;
    p.renderScale = PERF.renderScale;
    p.dpr = gl.getPixelRatio();
    p.obsVisible = RENDER_STATS.obsVisible;
    p.obsMounted = RENDER_STATS.obsMounted;
    p.notesDrawn = RENDER_STATS.notes;
    p.rails = RENDER_STATS.rails;
    (window as unknown as { __synapsePerf: PerfProbe }).__synapsePerf = p;
  });

  return null;
}
